import { Link } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import {
  PackageCheck,
  Wrench,
  Layers,
  Boxes,
  MonitorSmartphone,
  Cable,
  ArrowRight,
  Cpu,
  Tv,
  Keyboard,
  HardDrive,
  Headphones as HeadphonesIcon,
  Mouse,
  Router,
} from "lucide-react";

const categories = [
  {
    icon: PackageCheck,
    title: "Open Box Electronics",
    slug: "open-box",
    desc: "Products that have been opened but remain in excellent condition. Verified contents and inspected before listing.",
  },
  {
    icon: Wrench,
    title: "Refurbished Devices",
    slug: "refurbished",
    desc: "Professionally inspected and tested products prepared for resale. Functionality verified.",
  },
  {
    icon: Layers,
    title: "Customer Returns",
    slug: "customer-returns",
    desc: "Products sourced from return inventory and carefully evaluated for condition and functionality.",
  },
  {
    icon: Boxes,
    title: "Overstock Inventory",
    slug: "overstock",
    desc: "Surplus inventory available at discounted pricing. New or like-new items from oversupply.",
  },
  {
    icon: MonitorSmartphone,
    title: "Media Devices",
    slug: "media-devices",
    desc: "Various media and entertainment hardware products. Hardware only — no subscriptions or services.",
  },
  {
    icon: Cable,
    title: "Electronic Accessories",
    slug: "accessories",
    desc: "HDMI cables, remotes, adapters, power supplies, and related accessories.",
  },
];

const sampleProducts = [
  { icon: Tv, name: "Display Hardware", grade: "Open Box" },
  { icon: Cpu, name: "Computing Components", grade: "Refurbished" },
  { icon: HardDrive, name: "Storage Devices", grade: "Refurbished" },
  { icon: Keyboard, name: "Input Devices", grade: "Open Box" },
  { icon: Router, name: "Networking Hardware", grade: "Overstock" },
  { icon: Mouse, name: "Computer Peripherals", grade: "Customer Return" },
  { icon: HeadphonesIcon, name: "Audio Hardware", grade: "Refurbished" },
  { icon: Cable, name: "Cables & Adapters", grade: "New" },
];

export default function Products() {
  return (
    <div data-testid="products-page">
      <PageHeader
        eyebrow="Products"
        breadcrumb="Products"
        title="Browse our hardware categories."
        subtitle="CNL Tech sells hardware products only. Each category is sourced, inspected, and graded so customers can shop with clarity."
      />

      {/* Disclaimer band */}
      <section className="bg-[#F9FAFB] border-b border-gray-200">
        <div className="cnl-container py-5 text-[13px] text-gray-600 flex items-start gap-3">
          <span className="inline-block h-2 w-2 rounded-full bg-[#D97706] mt-1.5 shrink-0" />
          <p>
            Product inventory varies. For current availability, pricing, and specifications,
            please <Link to="/contact" className="cnl-link font-semibold" data-testid="products-contact-link">contact us</Link>.
            CNL Tech sells hardware only — we do not provide subscriptions or services.
          </p>
        </div>
      </section>

      {/* Categories */}
      <section className="bg-white">
        <div className="cnl-container py-20 lg:py-24">
          <div className="mb-10 max-w-2xl">
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#2563EB]">
              Categories
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
              Six clearly defined hardware categories.
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {categories.map((cat, idx) => (
              <div
                key={cat.slug}
                className="group border border-gray-200 rounded-sm p-7 transition-all hover:border-[#0B192C] hover:cnl-shadow-md bg-white"
                data-testid={`product-category-${cat.slug}`}
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="flex h-11 w-11 items-center justify-center rounded-sm bg-[#0B192C] text-white">
                    <cat.icon size={20} />
                  </div>
                  <span className="mono text-[11px] font-semibold text-gray-400">
                    /0{idx + 1}
                  </span>
                </div>
                <h3 className="text-[19px] font-semibold tracking-tight text-[#0B192C]">
                  {cat.title}
                </h3>
                <p className="mt-2.5 text-[14px] text-gray-600 leading-relaxed">
                  {cat.desc}
                </p>
                <Link
                  to="/contact"
                  className="mt-6 inline-flex items-center gap-1.5 text-[13px] font-semibold text-[#0B192C] cnl-link"
                  data-testid={`category-inquire-${cat.slug}`}
                >
                  Inquire about availability <ArrowRight size={14} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Sample hardware types */}
      <section className="bg-[#F3F4F6] border-y border-gray-200">
        <div className="cnl-container py-20 lg:py-24">
          <div className="mb-10 max-w-2xl">
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#2563EB]">
              Hardware Types We Resell
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl font-bold tracking-tight text-[#0B192C]">
              Examples of items found in our inventory.
            </h2>
            <p className="mt-3 text-[14.5px] text-gray-600">
              Availability rotates regularly based on what we source from customer
              returns, liquidation, and overstock channels.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {sampleProducts.map((p) => (
              <div
                key={p.name}
                className="bg-white border border-gray-200 rounded-sm p-6 hover:border-[#0B192C] transition-colors"
                data-testid={`sample-product-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="flex h-10 w-10 items-center justify-center rounded-sm bg-gray-100 text-[#0B192C]">
                  <p.icon size={18} />
                </div>
                <h3 className="mt-4 text-[14.5px] font-semibold text-[#0B192C]">
                  {p.name}
                </h3>
                <span className="mt-2 inline-block text-[10.5px] font-semibold uppercase tracking-[0.16em] text-[#1E3E62]">
                  {p.grade}
                </span>
              </div>
            ))}
          </div>

          <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-t border-gray-200 pt-8">
            <p className="text-[14px] text-gray-600 max-w-xl">
              Looking for something specific? We can let you know if a particular
              hardware item is in stock or expected in upcoming inventory.
            </p>
            <Link
              to="/contact"
              data-testid="products-bottom-cta"
              className="inline-flex items-center gap-2 rounded-sm bg-[#0B192C] px-7 py-3.5 text-[12.5px] font-semibold uppercase tracking-[0.16em] text-white transition-colors hover:bg-[#1E3E62]"
            >
              Ask about availability <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
